<template>
  <div class="farabi-id-card-report">
    <!-- Control Bar -->
    <div class="no-print control-bar">
      <div class="control-bar__brand">
        <div class="brand-badge mr-2">
          <feather-icon icon="ShieldIcon" size="20" class="text-white" />
        </div>
        <div>
          <h4 class="mb-0 font-weight-bolder">
            Farabi Coaching Centre
            <span class="text-primary">| ID Card Generator</span>
          </h4>
        </div>
        <b-badge v-show="!loading" variant="light-primary" class="ml-2 px-1 font-medium-1">
          {{ reportItems.length }} Students Loaded
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print All Cards</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Exit</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5 loading-wrapper">
      <div class="loader-content">
        <b-spinner
          variant="primary"
          style="width: 4rem; height: 4rem"
          class="mb-3"
        />
        <h3 class="font-weight-bold text-primary">
          Generating Farabi Coaching ID Cards
        </h3>
        <p class="text-muted">
          Please wait while we process the student records...
        </p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5 error-wrapper">
      <div class="error-icon-bg mb-3">
        <feather-icon icon="AlertTriangleIcon" size="48" class="text-danger" />
      </div>
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button
        variant="outline-primary"
        @click="loadReportData"
        class="mt-3 round"
        >Try Again</b-button
      >
    </div>

    <!-- Cards Container -->
    <div v-else class="cards-grid">
      <div
        v-for="(student, index) in reportItems"
        :key="index"
        class="student-id-block"
      >
        <!-- Front Side Card -->
        <div class="card-side-wrapper">
          <div class="id-card-box front-side">
            <div class="card-header-red">MAIN CAMPUS</div>

            <div class="card-content-area">
              <!-- Header section inside content -->
              <div class="content-header-top">
                <div class="logo-area">
                  <img
                    :src="schoolLogo || '/logo.png'"
                    alt="Logo"
                    class="institute-logo"
                  />
                </div>
                <div class="title-area">
                  <h2 class="institute-name">FARABI COACHING</h2>
                  <h2 class="institute-name">CENTRE</h2>
                  <h3 class="card-type-label">STUDENT ID CARD</h3>
                </div>
              </div>

              <div class="card-divider-hairline"></div>

              <div class="content-main-body">
                <div class="data-section">
                  <div class="data-fields-list">
                    <div class="data-row gr-row">
                      <span class="field-label">G.R.NO.:</span>
                      <span class="gr-number-val">{{
                        student.grNo || student.gr || "---"
                      }}</span>
                      <span v-if="sessYear" class="sess-year-val">{{ sessYear }}</span>
                    </div>
                    <div class="data-row">
                      <span class="field-label">NAME:</span>
                      <span class="field-value name-text">{{
                        (student.name || "").toUpperCase()
                      }}</span>
                    </div>
                    <div class="data-row">
                      <span class="field-label">F.NAME:</span>
                      <span class="field-value">{{
                        (
                          student.father_name ||
                          student.fatherName ||
                          ""
                        ).toUpperCase()
                      }}</span>
                    </div>
                    <div class="data-row">
                      <span class="field-label">CLASS:</span>
                      <span class="field-value">{{
                        student.className || student.meta_class || "---"
                      }}</span>
                    </div>
                    <div class="data-row address-row">
                      <span class="field-label">ADDRESS:</span>
                      <span class="field-value address-text">{{
                        student.address_current || student.address || "N/A"
                      }}</span>
                    </div>
                  </div>
                </div>

                <div class="photo-section">
                  <div class="photo-container">
                    <img
                      :src="
                        student.picture || student.photo || defaultPlaceholder
                      "
                      alt="Student"
                      class="student-photo-img"
                      @error="handleImgError"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div class="card-footer-blue"></div>
          </div>
        </div>

        <!-- Back Side Card -->
        <div class="card-side-wrapper">
          <div class="id-card-box back-side">
            <div class="card-header-red"></div>
            <div class="card-content-area back-layout">
              <div class="back-branding">
                <img
                  :src="schoolLogo || '/logo.png'"
                  alt="Logo"
                  class="institute-logo-back"
                />
              </div>

              <div class="back-info-content">
                <div class="instruction-box">
                  <p class="return-instruction">
                    If found, please return to following address
                  </p>
                </div>

                <h4 class="institute-name-back">FARABI COACHING CENTRE</h4>
                <div class="address-box-back">
                  <p class="institute-address-back">
                    MC 851, PATHAR ROAD, GREEN TOWN, KARACHI.
                  </p>
                </div>
              </div>

              <div class="barcode-footer">
                <div class="barcode-wrapper">
                  <div class="barcode-font-visual">
                    *{{ student.grNo || student.gr || "0000" }}*
                  </div>
                  <div class="barcode-label">
                    GR: {{ student.grNo || student.gr || "0000" }}
                  </div>
                </div>
              </div>
            </div>
            <div class="card-footer-blue"></div>
          </div>
        </div>
      </div>

      <div
        v-if="reportItems.length === 0"
        class="text-center py-5 no-data-state"
      >
        <div class="no-data-icon">
          <feather-icon icon="InboxIcon" size="64" />
        </div>
        <h3 class="mt-2 font-weight-bold">No Students Found</h3>
        <p class="text-muted">
          Try adjusting your filters to find student records.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "FarabiCoaIDCard",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportItems: [],
      schoolName: "FARABI COACHING CENTRE",
      schoolLogo: null,
      db: "",
      cID: "",
      classID: 0,
      sectionID: 0,
      status: "present",
      sessYear: "",
      sessionID: 0,
      studentIDs: [], // Filter by specific student IDs when coming from grid
      reportDomain: (this.$store.state.report || "").replace(/_/g, ""),
      defaultPlaceholder:
        "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 150 180' width='150' height='180'%3E%3Crect width='150' height='180' fill='%23dce0e3'/%3E%3Ccircle cx='75' cy='65' r='30' fill='%237a8289'/%3E%3Cpath d='M 15 180 Q 15 115 75 115 Q 135 115 135 180 Z' fill='%237a8289'/%3E%3C/svg%3E",
    };
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData.db;
      this.cID = query.cID || 0;
      this.classID = query.cls || "";
      this.sectionID = query.sec || "";
      // Parse studentIDs from grid.vue routing (comma-separated)
      if (query.studentIDs) {
        this.studentIDs = query.studentIDs
          .split(",")
          .map((id) => parseInt(id))
          .filter((id) => !isNaN(id));
      }

      this.status =
        this.studentIDs.length > 0 ? "all" : query.status || "present";
      this.sessYear = query.sess || "2026-2027";
      this.sessionID = query.sessionID || 0;

      if (!this.db) {
        this.error = "System context missing. Please reload from dashboard.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const url = `https://apiv26.myskool.app/api/campuses`;
        const response = await axios.get(url, {
          params: { db: this.db, cId: this.cID },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          if (match) {
            this.schoolLogo = match.logo;
          }
        }
      } catch (e) {
        console.warn("Campus branding failed to load");
      }
    },
    normalizeStudentList(responseData) {
      if (!responseData) return [];
      if (!Array.isArray(responseData) && Array.isArray(responseData.data)) {
        return responseData.data;
      }
      if (Array.isArray(responseData)) return responseData;
      return [];
    },

    async loadReportData() {
      this.loading = true;
      this.error = null;

      const headers = {
        Authorization: `Bearer ${this.$store.state.userData.token}`,
        accept: "application/json",
      };

      const sessionID = this.sessionID || this.$store.state.sessionID || 0;

      try {
        let response;

        if (this.studentIDs.length > 0) {
          // From grid.vue: load only selected students
          const url = `${this.$store.state.domain}Students/LoadDataSelected`;
          response = await axios.get(url, {
            params: {
              db: this.db,
              cID: this.cID,
              ids: this.studentIDs.join(","),
              status: "all",
              sessionID,
            },
            headers,
          });
        } else {
          // From reports.vue: load all by class/section filters
          const url = `${this.$store.state.domain}Students/LoadDataAll`;
          response = await axios.get(url, {
            params: {
              db: this.db,
              cID: this.cID,
              clsID: this.classID || 0,
              section: this.sectionID || 0,
              status: this.status,
              sessionID,
            },
            headers,
          });
        }

        if (response.data && response.data.status === "success") {
          this.reportItems = this.normalizeStudentList(response.data.data);
        } else if (Array.isArray(response.data)) {
          this.reportItems = response.data;
        } else {
          this.error =
            response.data?.message ||
            "The server did not return valid student records.";
        }
      } catch (err) {
        console.error("Data retrieval error:", err);
        this.error = "Unable to connect to school records system.";
      } finally {
        this.loading = false;
      }
    },
    handleImgError(e) {
      e.target.src = this.defaultPlaceholder;
    },
    print() {
      window.print();
    },
    close() {
      if (this.studentIDs && this.studentIDs.length > 0) {
        this.$router.push({ name: "manage-studentgrid" });
      } else {
        this.$router.push({ name: "report" });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700;800;900&family=Inter:wght@400;500;600;700;800&family=Libre+Barcode+39&display=swap");

/* Calibrated for print: ~8.0cm × ~5.1cm physical (trimmed 2mm right + bottom) */
$card-w: 7.38cm;
$card-h: 4.7cm;
$card-header-h: 4.2mm;
$card-footer-h: 3.5mm;
$card-pair-gap: 8px;

.farabi-id-card-report {
  background: #f1f5f9;
  min-height: 100vh;
  padding: 2rem 3rem 4rem;
  font-family: "Inter", sans-serif;
  color: #1e293b;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  padding: 1rem 2rem;
  margin-bottom: 2.5rem;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid #e2e8f0;

  &__brand {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .brand-badge {
    background: linear-gradient(135deg, #0071bc 0%, #0284c7 100%);
    padding: 10px;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 113, 188, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  padding: 0.7rem 1.6rem;
  border-radius: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  font-size: 0.88rem;

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #0071bc 0%, #0284c7 100%);
  color: #ffffff;
  box-shadow: 0 4px 14px rgba(0, 113, 188, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 22px rgba(0, 113, 188, 0.4);
    color: #ffffff;
  }
}

.btn-close {
  background: #ffffff;
  color: #64748b;
  border: 1.5px solid #e2e8f0;

  &:hover {
    background: #f8fafc;
    color: #ef4444;
    border-color: #fca5a5;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }
}

/* ID Card Grid Layout */
.cards-grid {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: calc(#{$card-w} * 2 + #{$card-pair-gap});
  margin: 0 auto;
}

.student-id-block {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  flex-wrap: nowrap;
  flex-shrink: 0;
  gap: $card-pair-gap;
  padding: 16px 0;
  border-bottom: 1px dashed #cbd5e1;

  &:last-child {
    border-bottom: none;
  }
}

.card-side-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 0 0 $card-w;
  width: $card-w;
  min-width: $card-w;
  flex-shrink: 0;
}

/* ─── CARD DIMENSIONS: ~8.2cm × 5.3cm physical print (with 1.45 zoom) ─ */
.id-card-box {
  width: $card-w;
  min-width: $card-w;
  max-width: $card-w;
  height: $card-h;
  min-height: $card-h;
  max-height: $card-h;
  flex-shrink: 0;
  background: #ffffff;
  border: 0.3mm solid #94a3b8;
  border-radius: 2.2mm; // CR80 standard rounded corner
  position: relative;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
}

.card-header-red {
  background: #ed1c24;
  color: #ffffff;
  text-align: center;
  font-weight: 800;
  font-size: 6.8pt;
  height: $card-header-h;
  min-height: $card-header-h;
  max-height: $card-header-h;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  letter-spacing: 0.8px;
  font-family: "Outfit", sans-serif;
  text-transform: uppercase;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.card-footer-blue {
  background: #0071bc;
  height: $card-footer-h;
  min-height: $card-footer-h;
  max-height: $card-footer-h;
  flex-shrink: 0;
  width: 100%;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.card-content-area {
  flex: 1;
  height: calc(#{$card-h} - #{$card-header-h} - #{$card-footer-h});
  max-height: calc(#{$card-h} - #{$card-header-h} - #{$card-footer-h});
  display: flex;
  flex-direction: column;
  padding: 1.5mm 2.8mm 1.2mm 2.8mm;
  box-sizing: border-box;
  overflow: hidden;
}

/* Header inside content */
.content-header-top {
  display: flex;
  align-items: center;
  height: 12.2mm;
  min-height: 12.2mm;
  max-height: 12.2mm;
  flex-shrink: 0;
}

.logo-area {
  width: 11.5mm;
  height: 11.5mm;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 2mm;
  flex-shrink: 0;

  .institute-logo {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    image-rendering: -webkit-optimize-contrast;
  }
}

.title-area {
  flex: 1;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  line-height: 1;
}

.institute-name {
  color: #0071bc;
  font-size: 9.2pt;
  font-weight: 900;
  margin: 0;
  line-height: 1.05;
  text-transform: uppercase;
  font-family: "Outfit", sans-serif;
  letter-spacing: 0.2px;
}

.card-type-label {
  color: #ed1c24;
  font-size: 5.6pt;
  font-weight: 800;
  font-style: italic;
  margin: 0.3mm 0 0;
  letter-spacing: 0.5px;
}

.card-divider-hairline {
  height: 0.6px;
  background: linear-gradient(90deg, #0071bc, #ed1c24, #0071bc);
  margin: 0.5mm 0 0.8mm;
  flex-shrink: 0;
}

/* Main Body Section */
.content-main-body {
  display: flex;
  flex: 1;
  justify-content: space-between;
  align-items: stretch;
  overflow: hidden;
  height: calc(100% - 14.1mm);
}

.data-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-right: 2mm;
  overflow: hidden;
}

.data-fields-list {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
}

.data-row {
  display: flex;
  align-items: baseline;
  font-size: 5.8pt;
  line-height: 1.15;
}

.field-label {
  font-weight: 800;
  width: 13.5mm;
  min-width: 13.5mm;
  max-width: 13.5mm;
  color: #000;
  flex-shrink: 0;
  font-size: 5.6pt;
  letter-spacing: 0.1px;
}

.field-value {
  font-weight: 700;
  color: #000;
  font-size: 5.8pt;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.name-text {
  font-size: 6.2pt;
  font-weight: 800;
  color: #000;
}

.gr-row {
  display: flex;
  align-items: center;
}

.gr-number-val {
  font-size: 6pt;
  font-weight: 800;
  color: #000;
}

.sess-year-val {
  font-size: 5.2pt;
  font-weight: 800;
  color: #0071bc;
  margin-left: auto;
  background: #f0f7ff;
  border: 0.4px solid #bae6fd;
  border-radius: 1.5px;
  padding: 0.2mm 1.2mm;
  line-height: 1.2;
}

.address-row {
  align-items: flex-start;
}

.address-text {
  font-size: 5.1pt;
  line-height: 1.2;
  height: 2.4em;
  max-height: 2.4em;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
  color: #1e293b;
  font-weight: 600;
}

/* Photo Section */
.photo-section {
  width: 19.5mm;
  min-width: 19.5mm;
  max-width: 19.5mm;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  flex-shrink: 0;
}

.photo-container {
  width: 18mm;
  height: 22mm;
  max-height: 22mm;
  border: 1.2px solid #0071bc;
  border-radius: 2px;
  overflow: hidden;
  background: #f8fafc;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;

  .student-photo-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    image-rendering: -webkit-optimize-contrast;
  }
}

/* Back Side Layout */
.back-layout {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  text-align: center;
  padding: 1.8mm 3.5mm 0.8mm 3.5mm;
}

.back-branding {
  height: 9mm;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  .institute-logo-back {
    max-height: 9mm;
    max-width: 22mm;
    object-fit: contain;
  }
}

.back-info-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.instruction-box {
  margin-bottom: 0.5mm;
}

.return-instruction {
  color: #ed1c24;
  font-size: 5.6pt;
  font-weight: 700;
  margin: 0;
  letter-spacing: 0.2px;
  text-transform: uppercase;
}

.institute-name-back {
  color: #0071bc;
  font-size: 7.6pt;
  font-weight: 900;
  margin: 0 0 0.5mm 0;
  font-family: "Outfit", sans-serif;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

.address-box-back {
  margin: 0 0 1mm 0;
}

.institute-address-back {
  font-size: 5.6pt;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.2px;
}

.barcode-footer {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: auto;
  margin-bottom: 0.4mm;
  flex-shrink: 0;
}

.barcode-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.barcode-font-visual {
  font-family: "Libre Barcode 39", cursive;
  font-size: 22pt;
  color: #000;
  letter-spacing: 1px;
  line-height: 0.7;
}

.barcode-label {
  font-size: 5.5pt;
  font-weight: 800;
  color: #000;
  letter-spacing: 1px;
  margin: 0.3mm 0 0 0;
  line-height: 1;
  padding-bottom: 0.2mm;
}

/* States */
.loading-wrapper,
.error-wrapper,
.no-data-state {
  background: white;
  border-radius: 20px;
  padding: 4rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
}
</style>

<!-- ─── SCREEN + PRINT RULES ─────────────────────────────────────── -->
<style lang="scss">
$card-w: 7.38cm;
$card-h: 4.7cm;
$card-pair-gap: 8px;
$card-pair-gap-print: 2mm;
$screen-preview-scale: 1.45;

@media screen {
  .farabi-id-card-report .cards-grid {
    zoom: $screen-preview-scale;
    max-width: calc((#{$card-w} * 2 + #{$card-pair-gap}) * #{$screen-preview-scale}) !important;
    width: 100% !important;
    padding-bottom: 24px;
  }

  .farabi-id-card-report .student-id-block {
    flex-wrap: nowrap !important;
    flex-shrink: 0 !important;
    width: max-content !important;
    max-width: none !important;
    margin-left: auto !important;
    margin-right: auto !important;
  }

  .farabi-id-card-report .card-side-wrapper {
    flex: 0 0 $card-w !important;
    width: $card-w !important;
    min-width: $card-w !important;
    max-width: $card-w !important;
    flex-shrink: 0 !important;
  }

  .farabi-id-card-report .id-card-box {
    width: $card-w !important;
    min-width: $card-w !important;
    max-width: $card-w !important;
    height: $card-h !important;
    min-height: $card-h !important;
    max-height: $card-h !important;
    flex-shrink: 0 !important;
    box-sizing: border-box !important;
  }
}

@media print {
  @page {
    size: A4 portrait;
    margin: 8mm 6mm !important;
  }

  *,
  *::before,
  *::after {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
    box-sizing: border-box !important;
  }

  html,
  body,
  #app,
  #app.h-100 {
    width: 100% !important;
    height: auto !important;
    min-height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #ffffff !important;
    overflow: visible !important;
  }

  .no-print,
  .control-bar,
  .side-indicator,
  header,
  footer,
  nav,
  aside,
  .navbar,
  [class*="sidebar"] {
    display: none !important;
  }

  .farabi-id-card-report {
    padding: 0 !important;
    margin: 0 !important;
    background: transparent !important;
    min-height: auto !important;
    height: auto !important;
    width: 100% !important;
  }

  .farabi-id-card-report .cards-grid {
    display: flex !important;
    flex-direction: column !important;
    width: 100% !important;
    margin: 0 auto !important;
    padding: 0 !important;
    padding-top: 6mm !important;
    gap: 6mm !important;
    zoom: $screen-preview-scale !important;
    transform: none !important;
  }

  .farabi-id-card-report .student-id-block {
    display: flex !important;
    flex-direction: row !important;
    justify-content: center !important;
    align-items: flex-start !important;
    flex-wrap: nowrap !important;
    flex-shrink: 0 !important;
    width: max-content !important;
    gap: $card-pair-gap-print !important;
    padding: 0 !important;
    margin: 0 auto !important;
    border-bottom: none !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }

  .farabi-id-card-report .card-side-wrapper {
    display: inline-flex !important;
    flex-direction: column !important;
    align-items: center !important;
    flex: 0 0 $card-w !important;
    margin: 0 !important;
    padding: 0 !important;
    width: $card-w !important;
    min-width: $card-w !important;
    max-width: $card-w !important;
    flex-shrink: 0 !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    page-break-after: auto !important;
    break-after: auto !important;
  }

  .farabi-id-card-report .id-card-box {
    width: $card-w !important;
    min-width: $card-w !important;
    max-width: $card-w !important;
    height: $card-h !important;
    min-height: $card-h !important;
    max-height: $card-h !important;
    flex-shrink: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    border: 0.25mm solid #cbd5e1 !important;
    border-radius: 2mm !important;
    box-shadow: none !important;
    box-sizing: border-box !important;
    overflow: hidden !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    page-break-after: auto !important;
    break-after: auto !important;
  }

  .card-header-red {
    background: #ed1c24 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .card-footer-blue {
    background: #0071bc !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .photo-container {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
</style>
